"use client";

import React, { useState } from "react";
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Typography,
    Container,
    styled,
    Divider,
    Box,
    Button,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTranslation } from "react-i18next";

const StyledAccordion = styled(Accordion)(({ theme }) => ({
    boxShadow: "none",
    "&:before": { display: "none" },
    "&:not(:last-child)": { borderBottom: `1px solid ${theme.palette.divider}` },
    background: "transparent",
}));

const StyledAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
    padding: theme.spacing(2, 0),
    "& .MuiAccordionSummary-content": { margin: theme.spacing(0) },
    "& .MuiAccordionSummary-expandIconWrapper": {
        color: theme.palette.text.secondary,
    },
}));

const StyledAccordionDetails = styled(AccordionDetails)(({ theme }) => ({
    padding: theme.spacing(0, 0, 2, 0),
}));

const INITIAL_FAQ_COUNT = 6;

const AIAgentFAQs = () => {
    const { t, i18n } = useTranslation();
    const isArabic = i18n.language === "ar";

    const [expanded, setExpanded] = useState(0);
    const [showAll, setShowAll] = useState(false);

    const faqsRaw = t("AIAgentFAQs.faqs", { returnObjects: true });
    const faqs = Array.isArray(faqsRaw) ? faqsRaw : [];
    const visibleFaqs = showAll ? faqs : faqs.slice(0, INITIAL_FAQ_COUNT);

    const handleChange = (index) => (_, isExpanded) => {
        setExpanded(isExpanded ? index : false);
    };

    return (
        <Box
            sx={{
                width: "100%",
                backgroundColor: "#fff",
                direction: isArabic ? "rtl" : "ltr",
            }}
        >
            <Container
                maxWidth="md"
                sx={{ py: { xs: 6, md: 5 }, px: { xs: 2.5, sm: 3 } }}
            >
                {/* Heading Line 1 */}
                <Typography
                    component="h2"
                    sx={{
                        mb: 0.5,
                        fontSize: { xs: "1.6rem", sm: "1.9rem", md: "2.1rem" },
                        color: "#2B343B",
                        fontFamily: "var(--font-plusjakarta)",
                        fontWeight: "bold",
                        textAlign: isArabic ? "right" : "left",
                        lineHeight: 1.25,
                        mt: { xs: 1.5, md: 3 }
                    }}
                >
                    {t("AIAgentFAQs.headingLine1")}
                </Typography>

                {/* Heading Line 2 */}
                <Typography
                    component="h2"
                    sx={{
                        mb: 2,
                        fontSize: { xs: "1.6rem", sm: "1.9rem", md: "2.1rem" },
                        color: "#9B1FF0",
                        fontFamily: "var(--font-plusjakarta)",
                        fontWeight: "bold",
                        textAlign: isArabic ? "right" : "left",
                        lineHeight: 1.25,
                    }}
                >
                    {t("AIAgentFAQs.headingLine2")}
                </Typography>


                <Divider sx={{ mb: 0.5 }} />

                {/* Accordion items */}
                {visibleFaqs.map((faq, index) => (
                    <StyledAccordion
                        key={index}
                        disableGutters
                        expanded={expanded === index}
                        onChange={handleChange(index)}
                    >
                        <StyledAccordionSummary
                            expandIcon={<ExpandMoreIcon sx={{ color: "#6B6585" }} />}
                            // ✅ Arabic mein icon left pe
                            sx={{
                                flexDirection: isArabic ? "row" : "row",
                                "& .MuiAccordionSummary-expandIconWrapper": {
                                    marginRight: isArabic ? 0 : "auto",
                                    marginLeft: isArabic ? "auto" : 0,
                                    transform: "none",
                                    "&.Mui-expanded": { transform: "rotate(180deg)" },
                                },
                            }}
                        >
                            <Typography
                                sx={{
                                    fontWeight: 400,
                                    fontFamily: "var(--font-outfit)",
                                    fontSize: { xs: "13px", sm: "15px", md: "16px" },
                                    lineHeight: 1.6,
                                    color: "#0F172A",
                                    pr: isArabic ? 0 : 2,
                                    pl: isArabic ? 2 : 0,
                                    textAlign: isArabic ? "right" : "left",
                                }}
                            >
                                {faq.question}
                            </Typography>
                        </StyledAccordionSummary>
                        <StyledAccordionDetails>
                            <Typography
                                sx={{
                                    fontFamily: "var(--font-outfit)",
                                    fontSize: { xs: "12px", sm: "14px", md: "15px" },
                                    color: "#47405A",
                                    lineHeight: 1.75,
                                    textAlign: isArabic ? "right" : "left",
                                }}
                            >
                                {faq.answer}
                            </Typography>
                        </StyledAccordionDetails>
                    </StyledAccordion>
                ))}

                {/* See More / Less */}
                {faqs.length > INITIAL_FAQ_COUNT && (
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            mt: { xs: 3, md: 5 },
                            mb: { xs: 2, md: 5 }
                        }}
                    >
                        <Button
                            onClick={() => setShowAll(!showAll)}
                            sx={{
                                bgcolor: "#fff",
                                color: "#5B6B77",
                                textTransform: "none",
                                px: 3,
                                py: 1.4,
                                fontFamily: "var(--font-outfit)",
                                fontSize: { xs: "0.85rem", md: "0.95rem" },
                                fontWeight: 500,
                                borderRadius: "28px",
                                border: "1px solid #E2E8F0",
                                boxShadow: "none",
                                "&:hover": {
                                    bgcolor: "#F5F3FF",
                                    borderColor: "#6D50E0",
                                    color: "#6D50E0",
                                    boxShadow: "none",
                                },
                                transition: "all 0.2s ease",
                            }}
                        >
                            {showAll ? t("AIAgentFAQs.seeLess") : t("AIAgentFAQs.seeMore")}
                        </Button>
                    </Box>
                )}
            </Container>
        </Box>
    );
};

export default AIAgentFAQs;
